/**
 * Login Page
 *
 * V63: 비밀번호 재설정 기능 — O365(works@ngle.co.kr) 커스텀 SMTP 발송
 */

import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { auth, functions, isDemo } from '../firebase/config';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // V63: 비밀번호 재설정 상태
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');

  // 데모 모드에서는 자동으로 메인 페이지로 이동
  useEffect(() => {
    if (isDemo) {
      // 약간의 딜레이 후 자동 이동 (사용자가 페이지를 볼 수 있도록)
      const timer = setTimeout(() => {
        navigate('/');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [navigate]);

  const getErrorMessage = (errorCode: string): string => {
    const errorMessages: Record<string, string> = {
      'auth/user-not-found': '가입되지 않은 이메일입니다.',
      'auth/wrong-password': '비밀번호가 올바르지 않습니다.',
      'auth/invalid-credential': '이메일 또는 비밀번호가 올바르지 않습니다.',
      'auth/invalid-login-credentials': '이메일 또는 비밀번호가 올바르지 않습니다.',
      'auth/invalid-email': '이메일 형식이 올바르지 않습니다.',
      'auth/too-many-requests': '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.',
      'auth/user-disabled': '계정이 비활성화되었습니다. 퇴사 처리 또는 계정 상태를 확인해주세요.',
    };

    return errorMessages[errorCode] || '로그인에 실패했습니다. 잠시 후 다시 시도해주세요.';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 데모 모드: Firebase 로그인 시도 없이 바로 이동
      if (isDemo || !auth) {
        navigate('/');
        return;
      }

      // V287: 제출 시 trim 처리 (붙여넣기/스페이스 공백 대응)
      const trimmedUsername = username.trim();
      const trimmedPassword = password.trim();
      if (!trimmedUsername || !trimmedPassword) {
        setError('아이디와 비밀번호를 입력해주세요.');
        return;
      }

      // 실제 Firebase 로그인
      const email = `${trimmedUsername}@ngle.co.kr`;
      await signInWithEmailAndPassword(auth, email, trimmedPassword);
      navigate('/');
    } catch (err: any) {
      // V82.7 (rollback): MFA 분기 제거 — Identity Platform MFA 비활성

      // 원본 에러는 콘솔에만 출력
      console.error('로그인 오류:', err.code, err.message);

      // 사용자에게는 한글 메시지만 표시
      const userMessage = getErrorMessage(err.code || 'unknown');
      setError(userMessage);
    } finally {
      setLoading(false);
    }
  };

  // V63: 비밀번호 재설정 메일 발송
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');

    const trimmed = resetEmail.trim();
    if (!trimmed) {
      setResetError('아이디를 입력해주세요.');
      return;
    }

    if (!auth) {
      setResetError('시스템 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setResetLoading(true);

    try {
      const email = `${trimmed}@ngle.co.kr`;

      if (!functions) {
        setResetError('시스템 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      // V63: CF 호출 → O365(works@ngle.co.kr)로 메일 발송
      const sendResetEmail = httpsCallable(functions, 'sendPasswordResetEmailCustom');
      await sendResetEmail({ email });

      // 계정 존재 여부를 노출하지 않는 통일 메시지
      setResetMessage('가입된 이메일이면 비밀번호 재설정 메일이 발송됩니다. 메일함을 확인해주세요.');
      setResetEmail('');
    } catch (err: any) {
      console.error('비밀번호 재설정 오류:', err);
      setResetError('메일 발송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setResetLoading(false);
    }
  };

  // V63: 재설정 폼 열기/닫기
  const openResetForm = () => {
    setShowResetForm(true);
    setResetEmail(username.trim()); // 로그인 폼의 아이디를 미리 채움
    setResetError('');
    setResetMessage('');
  };

  const closeResetForm = () => {
    setShowResetForm(false);
    setResetEmail('');
    setResetError('');
    setResetMessage('');
  };

  return (
    <div className="bg-gradient" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div className="card" style={{
        maxWidth: '500px',
        width: '100%',
        padding: '48px'
      }}>
        {/* 로고 영역 */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: 'bold',
            color: 'var(--color-gray-800)',
            marginBottom: '12px'
          }}>
            nGle Works
          </h1>
          <p style={{
            color: 'var(--color-gray-500)',
            fontSize: '16px'
          }}>
            통합 ERP 시스템
          </p>
        </div>

        {/* V63: 비밀번호 재설정 폼 */}
        {showResetForm ? (
          <div>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--color-gray-800)',
              marginBottom: '8px'
            }}>
              비밀번호 재설정
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'var(--color-gray-500)',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              아이디를 입력하시면 비밀번호 재설정 메일이 발송됩니다.
            </p>

            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '10px',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: 'var(--color-gray-700)'
                }}>
                  아이디
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                  <input
                    type="text"
                    className="input"
                    placeholder="아이디"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    autoFocus
                    style={{
                      fontSize: '15px',
                      height: '48px',
                      flex: 1,
                      minWidth: 0
                    }}
                  />
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: 'var(--color-gray-600)',
                    whiteSpace: 'nowrap'
                  }}>
                    @ngle.co.kr
                  </span>
                </div>
              </div>

              {/* 성공 메시지 */}
              {resetMessage && (
                <div style={{
                  background: '#ecfdf5',
                  color: '#065f46',
                  padding: '14px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {resetMessage}
                </div>
              )}

              {/* 에러 메시지 */}
              {resetError && (
                <div style={{
                  background: '#fee2e2',
                  color: '#991b1b',
                  padding: '14px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '15px'
                }}>
                  {resetError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  className="btn"
                  onClick={closeResetForm}
                  style={{
                    flex: 1,
                    padding: '14px',
                    fontSize: '15px',
                    fontWeight: '500',
                    background: 'var(--color-gray-100)',
                    color: 'var(--color-gray-700)',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  돌아가기
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    flex: 1,
                    padding: '14px',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}
                  disabled={resetLoading || !resetEmail.trim()}
                >
                  {resetLoading ? '발송 중...' : '메일 발송'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            {/* 로그인 폼 */}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '10px',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: 'var(--color-gray-700)'
                }}>
                  아이디
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                  <input
                    type="text"
                    className="input"
                    placeholder="아이디"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    style={{
                      fontSize: '15px',
                      height: '48px',
                      flex: 1,
                      minWidth: 0
                    }}
                  />
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: 'var(--color-gray-600)',
                    whiteSpace: 'nowrap'
                  }}>
                    @ngle.co.kr
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '10px',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: 'var(--color-gray-700)'
                }}>
                  비밀번호
                </label>
                <input
                  type="password"
                  className="input"
                  placeholder="비밀번호를 입력하세요"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    fontSize: '15px',
                    height: '48px'
                  }}
                />
              </div>

              {/* V63: 비밀번호 재설정 링크 */}
              <div style={{ textAlign: 'right', marginBottom: '20px' }}>
                <button
                  type="button"
                  onClick={openResetForm}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-gray-500)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    padding: '4px 0',
                    textDecoration: 'underline'
                  }}
                >
                  비밀번호를 잊으셨나요?
                </button>
              </div>

              {error && (
                <div style={{
                  background: '#fee2e2',
                  color: '#991b1b',
                  padding: '14px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '15px'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '17px',
                  fontWeight: '600'
                }}
                disabled={loading}
              >
                {loading ? '로그인 중...' : '로그인'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
